/* eslint-disable react/prop-types */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Avatar,
  Typography,
  Button,
  IconButton,
  Box,
  Divider,
  Paper,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import GroupsIcon from "@mui/icons-material/Groups";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SecurityIcon from "@mui/icons-material/Security";
import BadgeIcon from "@mui/icons-material/Badge";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import LockIcon from "@mui/icons-material/Lock";
import { styled, useTheme, alpha } from "@mui/material/styles";
import { useUsersSettingService } from "@/services/useUsersSettingService";
import useGetMyCertificates from "@/hooks/useGetMyCertificates";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import { useTranslation } from "react-i18next";
import LoadAvatar from "@/pages/user/profile/loadAvatar";
import CertificateLoader from "./certificateLoader";
import ChangePassword from "./changePassword";
import Toast from "@/components/toast";
import CertificatesTable from "./certificatesTable";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid2";
import UserObservationsTable from "./UserObservationsTable";
import { useSession } from "@/hooks/useSession";
import { convertUTCToLocal } from "@/lib/utils/dateUtils";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.spacing(3),
    boxShadow: theme.shadows[24],
    border: "1px solid",
    borderColor: theme.palette.divider,
    background: theme.palette.background.paper,
    overflow: "hidden",
    maxHeight: "95vh",
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  margin: "0 auto",
  border: "4px solid",
  borderColor: theme.palette.background.paper,
  boxShadow: theme.shadows[8],
  fontWeight: 600,
  [theme.breakpoints.down("sm")]: {
    width: 80,
    height: 80,
    fontSize: "2rem",
    border: "3px solid",
  },
  [theme.breakpoints.between("sm", "md")]: {
    width: 100,
    height: 100,
    fontSize: "2.25rem",
  },
  [theme.breakpoints.up("md")]: {
    width: 120,
    height: 120,
    fontSize: "2.5rem",
  },
}));

const getInitials = (firstName, lastName) => {
  if (!firstName || !lastName) return "U";
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

const ViewUserProfile = ({ user, open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [openAvatarModal, setOpenAvatarModal] = useState(false);
  const [openPassword, setOpenPassword] = useState(false);
  const [certificateLoader, setCertificateLoader] = useState(false);
  const { session } = useSession();
  const { userDetails, detailIsLoading, refetchUserDetails } =
    useUsersSettingService(user?.id);
  const { MyCertificates, refetchMyCertificates } = useGetMyCertificates(
    user?.id
  );
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();

  const handleOpenAvatarModal = () => setOpenAvatarModal(true);
  const handleCloseAvatarModal = () => setOpenAvatarModal(false);
  const handleLoadCertificate = () => setCertificateLoader(true);
  const handleLoadCertificateClose = () => setCertificateLoader(false);

  if (detailIsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <StyledDialog open={open} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          py: { xs: 2, sm: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 2, sm: 3 },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Avatar
              src={userDetails?.avatar?.url}
              alt={userDetails?.username}
              sx={{
                width: { xs: 40, sm: 48, md: 56 },
                height: { xs: 40, sm: 48, md: 56 },
                border: { xs: "2px solid", sm: "3px solid" },
                borderColor: alpha(theme.palette.primary.main, 0.2),
                boxShadow: theme.shadows[4],
                fontSize: { xs: "1rem", sm: "1.15rem", md: "1.25rem" },
                fontWeight: 600,
              }}
            >
              {getInitials(userDetails?.first_name, userDetails?.last_name)}
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  mb: 0.5,
                  fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                  wordBreak: "break-word",
                }}
              >
                {userDetails?.first_name} {userDetails?.last_name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                <PersonIcon sx={{ fontSize: 16 }} />
                {t("viewUserProfile.registeredOnLabel")}{" "}
                {convertUTCToLocal(userDetails?.created_at)}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              bgcolor: alpha(theme.palette.text.primary, 0.08),
              "&:hover": {
                bgcolor: alpha(theme.palette.text.primary, 0.12),
                transform: "scale(1.05)",
              },
              transition: "all 0.2s ease",
            }}
            aria-label={t("viewUserProfile.closeButtonAria")}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {/* Avatar Section */}
          <Grid size={12}>
            <Paper
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                textAlign: "center",
                background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.8)} 0%, ${alpha(theme.palette.grey[100], 0.4)} 100%)`,
                ...(theme.palette.mode === "dark" && {
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[800], 0.8)} 0%, ${alpha(theme.palette.grey[900], 0.4)} 100%)`,
                }),
              }}
            >
              <StyledAvatar
                src={userDetails?.avatar?.url || ""}
                alt={userDetails?.username}
              >
                {!userDetails?.avatar?.url &&
                  getInitials(userDetails?.first_name, userDetails?.last_name)}
              </StyledAvatar>
              <Button
                onClick={handleOpenAvatarModal}
                startIcon={<PhotoCameraIcon />}
                sx={{
                  mt: 3,
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: theme.shadows[2],
                  "&:hover": {
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                {t("viewUserProfile.changeAvatarButton")}
              </Button>
            </Paper>
          </Grid>

          {/* Personal Information */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
              sx={{
                p: 4,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                height: "100%",
                /* background: alpha(theme.palette.primary.main, 0.02), */
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <EmailIcon color="primary" sx={{ fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                    }}
                  >
                    {t("viewUserProfile.personalInfoTitle")}
                  </Typography>
                </Box>
                <Button
                  onClick={() => setOpenPassword(true)}
                  startIcon={<LockIcon />}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {t("viewUserProfile.changePasswordButton")}
                </Button>
              </Box>

              <Grid container spacing={3}>
                <Grid size={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.grey[100], 0.5),
                      ...(theme.palette.mode === "dark" && {
                        bgcolor: alpha(theme.palette.grey[800], 0.5),
                      }),
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                      }}
                    >
                      {t("viewUserProfile.fullNameLabel")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mt: 0.5 }}
                    >
                      {userDetails?.first_name} {userDetails?.last_name}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.grey[100], 0.5),
                      ...(theme.palette.mode === "dark" && {
                        bgcolor: alpha(theme.palette.grey[800], 0.5),
                      }),
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                      }}
                    >
                      {t("viewUserProfile.usernameLabel")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mt: 0.5 }}
                    >
                      {userDetails?.username}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.grey[100], 0.5),
                      ...(theme.palette.mode === "dark" && {
                        bgcolor: alpha(theme.palette.grey[800], 0.5),
                      }),
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                      }}
                    >
                      {t("viewUserProfile.departmentLabel")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mt: 0.5 }}
                    >
                      {userDetails?.department?.name ||
                        t("viewUserProfile.noDepartmentText")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.grey[100], 0.5),
                      ...(theme.palette.mode === "dark" && {
                        bgcolor: alpha(theme.palette.grey[800], 0.5),
                      }),
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                      }}
                    >
                      {t("viewUserProfile.managerLabel")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mt: 0.5 }}
                    >
                      {userDetails?.manager
                        ? `${userDetails.manager.first_name} ${userDetails.manager.last_name} (@${userDetails.manager.username})`
                        : t("viewUserProfile.noManagerText")}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* User Groups and Sites */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "100%",
              }}
            >
              {/* User Groups */}
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  flexGrow: 1,
                  /* background: alpha(theme.palette.info.main, 0.02), */
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
                >
                  <GroupsIcon color="info" sx={{ fontSize: 24 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                    }}
                  >
                    {t("viewUserProfile.userGroupsTitle")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {userDetails?.user_groups?.length > 0 ? (
                    userDetails.user_groups.map((g) => (
                      <Tooltip
                        key={g.id}
                        title={t("viewUserProfile.groupTooltip", {
                          groupName: g.name,
                        })}
                      >
                        <Chip
                          label={g.name}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: "info.main",
                            border: "1px solid",
                            borderColor: alpha(theme.palette.info.main, 0.2),
                            fontWeight: 500,
                          }}
                          aria-label={t("viewUserProfile.groupChipAria", {
                            groupName: g.name,
                          })}
                        />
                      </Tooltip>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No groups assigned
                    </Typography>
                  )}
                </Box>
              </Paper>

              {/* Sites */}
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  flexGrow: 1,
                  /*  background: alpha(theme.palette.success.main, 0.02), */
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
                >
                  <LocationOnIcon color="success" sx={{ fontSize: 24 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                    }}
                  >
                    {t("viewUserProfile.sitesTitle")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {userDetails?.sites?.length > 0 ? (
                    userDetails.sites.map((s) => (
                      <Tooltip key={s.id} title={s.name}>
                        <Chip
                          label={s.name}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            color: "success.main",
                            border: "1px solid",
                            borderColor: alpha(theme.palette.success.main, 0.2),
                            fontWeight: 500,
                          }}
                          aria-label={t("viewUserProfile.siteChipAria", {
                            siteName: s.name,
                          })}
                        />
                      </Tooltip>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No sites assigned
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>
          </Grid>

          {/* Document Visibility */}
          <Grid size={12}>
            <Paper
              sx={{
                p: 4,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
              >
                <SecurityIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  {t("viewUserProfile.documentVisibilityTitle")}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {userDetails?.document_visibility_infos?.map((setting) => (
                  <Grid key={setting.id} size={{ xs: 12, sm: 6 }}>
                    <Paper
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        bgcolor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "text.primary",
                        }}
                      >
                        {setting?.document_type}
                      </Typography>
                      <Chip
                        label={setting.visibility_type}
                        variant="filled"
                        color="primary"
                        size="small"
                        sx={{
                          borderRadius: 2,
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}
                        aria-label={t("viewUserProfile.visibilityChipAria", {
                          documentType: setting.document_type,
                          visibilityType: setting.visibility_type,
                        })}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Certificates */}
          <Grid size={12}>
            <Paper
              sx={{
                p: 4,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                background: alpha(theme.palette.secondary.main, 0.02),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <BadgeIcon color="secondary" sx={{ fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                    }}
                  >
                    {t("viewUserProfile.certificatesTitle")}
                  </Typography>
                </Box>
                {session?.user_type === "ORG_ADMIN" && (
                  <Button
                    variant="contained"
                    startIcon={<BadgeIcon />}
                    onClick={handleLoadCertificate}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      boxShadow: theme.shadows[2],
                      "&:hover": {
                        boxShadow: theme.shadows[4],
                      },
                    }}
                  >
                    {t("viewUserProfile.addCertificateButton")}
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 3 }} />
              <CertificatesTable
                userName={`${user.first_name} ${user.last_name}`}
                certificates={MyCertificates}
                userId={user.id}
                handleApiError={handleApiError}
                setToast={setToast}
                onCertificateDelete={refetchMyCertificates}
                isOwnProfile={false}
              />
            </Paper>
          </Grid>

          {/* Observations */}
          <Grid size={12}>
            <Paper
              sx={{
                p: 4,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                background: alpha(theme.palette.error.main, 0.02),
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
              >
                <AssignmentIcon color="error" sx={{ fontSize: 28 }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  {t("viewUserProfile.observationsTitle")}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <UserObservationsTable
                userId={user.id}
                userName={`${user.first_name} ${user.last_name}`}
                onError={handleApiError}
              />
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Modals */}
      {certificateLoader && (
        <CertificateLoader
          open={certificateLoader}
          onClose={handleLoadCertificateClose}
          userId={user.id}
          setToast={setToast}
          handleApiError={handleApiError}
          onCertificateLoad={refetchMyCertificates}
        />
      )}

      {openAvatarModal && (
        <LoadAvatar
          open={openAvatarModal}
          onClose={handleCloseAvatarModal}
          user={user}
          avatarUrl={userDetails?.avatar?.url}
          setToast={setToast}
          handleApiError={handleApiError}
          onAvatarUpload={refetchUserDetails}
        />
      )}

      {openPassword && (
        <ChangePassword
          open={openPassword}
          onClose={() => setOpenPassword(false)}
          setToast={setToast}
          toast={toast}
        />
      )}

      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </StyledDialog>
  );
};

export default ViewUserProfile;
